import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.js';
import { iconChevron, iconChevronLeft } from './generated/icons.js';
import { cx } from './util.js';

export interface DatePickerProps {
  label: string;
  /** ISO date, yyyy-mm-dd. */
  value: string | null;
  onChange: (iso: string) => void;
  /** Dates before this (ISO) are disabled. */
  min?: string;
  max?: string;
  locale?: string;
  previousMonth?: string;
  nextMonth?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${String(y)}-${pad(m + 1)}-${pad(d)}`;

/**
 * A month grid you can drive from the keyboard: arrows move by a day or a week, Page Up and
 * Down by a month, Home and End to the ends of the week, Enter picks. Dates are plain ISO
 * strings, so no time zone can shift a day.
 */
export function DatePicker({
  label,
  value,
  onChange,
  min,
  max,
  locale = 'en-IN',
  previousMonth = 'Previous month',
  nextMonth = 'Next month',
}: DatePickerProps) {
  const today = new Date();
  const start = value ?? iso(today.getFullYear(), today.getMonth(), today.getDate());
  const [year, setYear] = useState(Number(start.slice(0, 4)));
  const [month, setMonth] = useState(Number(start.slice(5, 7)) - 1);
  const [focus, setFocus] = useState(start);
  const grid = useRef<HTMLDivElement>(null);
  const moved = useRef(false);

  const first = new Date(Date.UTC(year, month, 1));
  const lead = first.getUTCDay();
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const monthName = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(first);
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
      new Date(Date.UTC(2023, 0, 1 + i)),
    ),
  );
  const disabled = (d: string) => (min !== undefined && d < min) || (max !== undefined && d > max);

  const shift = (days_: number) => {
    const [y, m, d] = focus.split('-').map(Number) as [number, number, number];
    const next = new Date(Date.UTC(y, m - 1, d + days_));
    setFocus(iso(next.getUTCFullYear(), next.getUTCMonth(), next.getUTCDate()));
    setYear(next.getUTCFullYear());
    setMonth(next.getUTCMonth());
    moved.current = true;
  };
  const shiftMonth = (by: number) => {
    const [y, m, d] = focus.split('-').map(Number) as [number, number, number];
    const target = new Date(Date.UTC(y, m - 1 + by, 1));
    const last = new Date(
      Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
    ).getUTCDate();
    setFocus(iso(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, last)));
    setYear(target.getUTCFullYear());
    setMonth(target.getUTCMonth());
    moved.current = true;
  };
  useEffect(() => {
    if (moved.current) {
      grid.current?.querySelector<HTMLElement>(`[data-date="${focus}"]`)?.focus();
      moved.current = false;
    }
  }, [focus, year, month]);

  const cells: (number | null)[] = [
    ...Array<null>(lead).fill(null),
    ...Array.from({ length: days }, (_, i) => i + 1),
  ];
  return (
    <div className="k-date" role="group" aria-label={label}>
      <div className="k-date__head">
        <button
          type="button"
          className="k-iconbtn k-layer"
          aria-label={previousMonth}
          onClick={() => shiftMonth(-1)}
        >
          <Icon icon={iconChevronLeft} />
        </button>
        <p className="k-t-title-sm" aria-live="polite">
          {monthName}
        </p>
        <button
          type="button"
          className="k-iconbtn k-layer"
          aria-label={nextMonth}
          onClick={() => shiftMonth(1)}
        >
          <Icon icon={iconChevron} />
        </button>
      </div>
      <div
        ref={grid}
        role="grid"
        aria-label={monthName}
        className="k-date__grid"
        onKeyDown={(event) => {
          const map: Record<string, () => void> = {
            ArrowLeft: () => shift(-1),
            ArrowRight: () => shift(1),
            ArrowUp: () => shift(-7),
            ArrowDown: () => shift(7),
            PageUp: () => shiftMonth(-1),
            PageDown: () => shiftMonth(1),
            Home: () => shift(-new Date(`${focus}T00:00:00Z`).getUTCDay()),
            End: () => shift(6 - new Date(`${focus}T00:00:00Z`).getUTCDay()),
          };
          const action = map[event.key];
          if (action !== undefined) {
            event.preventDefault();
            action();
          }
        }}
      >
        <div role="row" className="k-date__row">
          {weekdays.map((w) => (
            <span key={w} role="columnheader" className="k-t-label-sm k-muted">
              {w}
            </span>
          ))}
        </div>
        {Array.from({ length: Math.ceil(cells.length / 7) }, (_, week) => (
          <div role="row" className="k-date__row" key={week}>
            {cells.slice(week * 7, week * 7 + 7).map((day, i) => {
              if (day === null) return <span key={`e${String(i)}`} role="gridcell" />;
              const date = iso(year, month, day);
              return (
                <button
                  key={date}
                  type="button"
                  role="gridcell"
                  data-date={date}
                  aria-selected={value === date}
                  disabled={disabled(date)}
                  tabIndex={focus === date ? 0 : -1}
                  className={cx(
                    'k-date__day k-layer k-t-body',
                    value === date && 'k-date__day--selected',
                  )}
                  onClick={() => {
                    setFocus(date);
                    onChange(date);
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
